/**
 * Dialer Component
 *
 * iPhone-style dialer interface with contact selection and custom prompts
 */

"use client"

import { FC, useState, useEffect, useRef } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Call2, CloseCircle2, LinearSearch, LinearChevronLeft,  Microphone, Edit, Trash2 } from "marshmallow-icons"
import { CRMContact } from "@/types/nexus"
import { toast } from "sonner"
import { normalizePhoneNumber } from "@/db/crm"

interface DialerProps {
  workspaceId: string
  onCallInitiated?: (contact: CRMContact, customPrompt?: string) => void
  triggerClassName?: string
}

interface DialerContact extends CRMContact {
  displayName: string
  displayPhone: string
  source?: 'contact' | 'lead'
  lead_id?: string
}

const DIALER_KEYS = [
  { key: '1', letters: '' },
  { key: '2', letters: 'ABC' },
  { key: '3', letters: 'DEF' },
  { key: '4', letters: 'GHI' },
  { key: '5', letters: 'JKL' },
  { key: '6', letters: 'MNO' },
  { key: '7', letters: 'PQRS' },
  { key: '8', letters: 'TUV' },
  { key: '9', letters: 'WXYZ' },
  { key: '*', letters: '' },
  { key: '0', letters: '+' },
  { key: '#', letters: '' }
]

const DIALER_KEYS_GRID = [
  [DIALER_KEYS[0], DIALER_KEYS[1], DIALER_KEYS[2]],
  [DIALER_KEYS[3], DIALER_KEYS[4], DIALER_KEYS[5]],
  [DIALER_KEYS[6], DIALER_KEYS[7], DIALER_KEYS[8]],
  [DIALER_KEYS[9], DIALER_KEYS[10], DIALER_KEYS[11]]
]

export const Dialer: FC<DialerProps> = ({
  workspaceId,
  onCallInitiated,
  triggerClassName
}) => {
  const [phoneNumber, setPhoneNumber] = useState("")
  const [contacts, setContacts] = useState<DialerContact[]>([])
  const [filteredContacts, setFilteredContacts] = useState<DialerContact[]>([])
  const [selectedContact, setSelectedContact] = useState<DialerContact | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [loading, setLoading] = useState(false)
  const [useCustomPrompt, setUseCustomPrompt] = useState(false)
  const [customPrompt, setCustomPrompt] = useState("")
  const [activeTab, setActiveTab] = useState("dialer")
  const [open, setOpen] = useState(false)
  const phoneInputRef = useRef<HTMLInputElement>(null)

  // Load contacts on open
  useEffect(() => {
    if (open && workspaceId) {
      loadContacts()
    }
  }, [open, workspaceId])

  // Filter contacts based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredContacts(contacts)
    } else {
      const query = searchQuery.toLowerCase()
      setFilteredContacts(
        contacts.filter(contact =>
          contact.displayName.toLowerCase().includes(query) ||
          contact.displayPhone.includes(searchQuery) ||
          (contact.email && contact.email.toLowerCase().includes(query))
        )
      )
    }
  }, [contacts, searchQuery])

  const loadContacts = async () => {
    try {
      setLoading(true)
      // Use API route which merges contacts and leads
      const response = await fetch(`/api/crm/contacts?workspace_id=${workspaceId}&limit=600`)
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error("API error:", response.status, errorData)
        throw new Error(errorData.error || 'Failed to fetch contacts')
      }

      const data = await response.json()
      console.log("Contacts API response:", data)
      
      const contactsData = data.contacts || []
      
      const processedContacts: DialerContact[] = contactsData.map((contact: CRMContact & { source?: 'contact' | 'lead', lead_id?: string }) => {
        const phone = contact.phone || contact.mobile || ''
        return {
          ...contact,
          displayName: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email || contact.company || 'Unknown',
          displayPhone: phone ? normalizePhoneNumber(phone) : '',
          source: contact.source,
          lead_id: contact.lead_id
        }
      })

      // Sort by name
      processedContacts.sort((a, b) => a.displayName.localeCompare(b.displayName))

      setContacts(processedContacts)
      setFilteredContacts(processedContacts)
      
      if (processedContacts.length === 0) {
        console.warn("No contacts found. Debug info:", data.debug || data)
      }
    } catch (error) {
      console.error("Error loading contacts:", error)
      toast.error("Failed to load people")
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (key: string) => {
    setPhoneNumber(prev => prev + key)
  }

  const handleBackspace = () => {
    setPhoneNumber(prev => prev.slice(0, -1))
  }

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow paste and manual input - strip non-numeric characters except +, *, #
    const value = e.target.value
    // Extract only digits for storage
    const digits = value.replace(/\D/g, '')
    setPhoneNumber(digits)
  }

  const handlePhoneInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    // Extract phone number from pasted text
    const digits = pastedText.replace(/\D/g, '')
    setPhoneNumber(digits)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow numbers, backspace, delete, arrow keys, and special characters
    if (e.key === 'Backspace' || e.key === 'Delete') {
      handleBackspace()
      e.preventDefault()
    } else if (e.key.match(/[0-9*#+]/)) {
      // Allow numbers and special dialer characters
      handleKeyPress(e.key)
      e.preventDefault()
    } else if (e.key === 'Enter') {
      // Prevent form submission
      e.preventDefault()
    }
    // Allow arrow keys, home, end for navigation
  }

  const handleContactSelect = (contact: DialerContact) => {
    setSelectedContact(contact)
    // Set phone number ready for dialing (use phone or mobile, normalized)
    const phone = contact.phone || contact.mobile || ''
    const normalizedPhone = phone ? normalizePhoneNumber(phone).replace(/\D/g, '') : ''
    setPhoneNumber(normalizedPhone)
    setActiveTab("dialer")
  }

  const handleClearContact = () => {
    setSelectedContact(null)
    setPhoneNumber("")
  }

  const handleCall = async () => {
    if (!phoneNumber.trim()) {
      toast.error("Please enter a phone number")
      return
    }

    const finalPrompt = useCustomPrompt && customPrompt.trim() ? customPrompt : undefined

    if (selectedContact) {
      onCallInitiated?.(selectedContact, finalPrompt)
    } else {
      // Create a temporary contact-like object for manual dial
      const tempContact = {
        id: `manual_${Date.now()}`,
        first_name: '',
        last_name: '',
        phone: phoneNumber,
        email: '',
        displayName: phoneNumber,
        displayPhone: phoneNumber
      } as DialerContact

      onCallInitiated?.(tempContact, finalPrompt)
    }

    setOpen(false)
    setPhoneNumber("")
    setSelectedContact(null)
    setCustomPrompt("")
    setUseCustomPrompt(false)
  }

  const formatPhoneNumber = (phone: string) => {
    // Basic US phone formatting
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
    }
    return phone
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          className={cn(
            "size-10 rounded-full bg-green-500 shadow-lg transition-all duration-200 hover:bg-green-600 hover:shadow-xl",
            triggerClassName
          )}
          title="Open Dialer"
        >
          <Call2 width={20} height={20} color="white" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        align="end" 
        side="bottom"
        sideOffset={8}
        className="mx-auto w-[400px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border-none bg-black p-0 text-white"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-800 bg-gray-900/50 p-4 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            {selectedContact && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearContact}
                className="size-8 p-1 text-white hover:bg-gray-700"
              >
                <LinearChevronLeft width={16} height={16} />
              </Button>
            )}
            <div className="flex items-center gap-2">
              {selectedContact ? (
                <>
                  <Avatar className="size-8">
                    <AvatarFallback className="bg-gray-600 text-xs text-white">
                      {selectedContact.first_name?.[0] || selectedContact.last_name?.[0] || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{selectedContact.displayName}</div>
                    <div className="truncate text-xs text-gray-400">{formatPhoneNumber(selectedContact.displayPhone)}</div>
                    {'source' in selectedContact && selectedContact.source && (
                      <Badge variant="outline" className="mt-0.5 border-gray-700 text-[10px] text-gray-400">
                        {selectedContact.source === 'lead' ? 'Contact' : 'Contact'}
                      </Badge>
                    )}
                  </div>
                </>
              ) : (
                <div className="font-medium text-white">Dialer</div>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            className="size-8 p-1 text-white hover:bg-gray-700"
          >
            <CloseCircle2 width={16} height={16} />
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList className="grid h-12 w-full grid-cols-2 rounded-none border-none bg-gray-900">
            <TabsTrigger
              value="dialer"
              className="rounded-none border-none text-gray-400 data-[state=active]:bg-gray-700 data-[state=active]:text-white"
            >
              Dialer
            </TabsTrigger>
            <TabsTrigger
              value="contacts"
              className="rounded-none border-none text-gray-400 data-[state=active]:bg-gray-700 data-[state=active]:text-white"
            >
              People
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dialer" className="mt-0 space-y-6 p-6">
            {/* Contact Details Section */}
            {selectedContact && (
              <div className="space-y-3 rounded-lg border border-gray-800 bg-gray-900/50 p-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <h3 className="truncate font-semibold text-white">{selectedContact.displayName}</h3>
                      {selectedContact.source && (
                        <Badge variant="outline" className="border-gray-700 text-[10px] text-gray-400">
                          {selectedContact.source === 'lead' ? 'Contact' : 'Contact'}
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-1.5 text-sm">
                      {selectedContact.company && (
                        <div className="text-gray-300">
                          <span className="text-gray-500">Company:</span> {selectedContact.company}
                        </div>
                      )}
                      {selectedContact.title && (
                        <div className="text-gray-300">
                          <span className="text-gray-500">Title:</span> {selectedContact.title}
                        </div>
                      )}
                      {selectedContact.email && (
                        <div className="text-gray-300">
                          <span className="text-gray-500">Email:</span> {selectedContact.email}
                        </div>
                      )}
                      {(selectedContact.phone || selectedContact.mobile) && (
                        <div className="text-gray-300">
                          <span className="text-gray-500">Phone:</span> {formatPhoneNumber(selectedContact.displayPhone)}
                        </div>
                      )}
                      {selectedContact.city && (
                        <div className="text-gray-300">
                          <span className="text-gray-500">Location:</span> {[selectedContact.city, selectedContact.state].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Phone Number Input with Backspace Button */}
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input
                  ref={phoneInputRef}
                  value={formatPhoneNumber(phoneNumber)}
                  onChange={handlePhoneInputChange}
                  onPaste={handlePhoneInputPaste}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  className="h-16 border-none bg-gray-900/50 px-6 text-center text-xl font-medium text-white placeholder:text-gray-500 focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-0"
                  placeholder="Enter phone number"
                />
              </div>
              <Button
                onClick={handleBackspace}
                disabled={!phoneNumber}
                size="icon"
                className="size-12 shrink-0 rounded-full border-none bg-red-600 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                title="Backspace"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                  <line x1="18" y1="9" x2="12" y2="15" />
                  <line x1="12" y1="9" x2="18" y2="15" />
                </svg>
              </Button>
            </div>

            {/* Keypad */}
            <div className="space-y-3">
              {DIALER_KEYS_GRID.map((row, rowIndex) => (
                <div key={rowIndex} className="flex justify-center gap-3">
                  {row.map((keyData) => (
                    <Button
                      key={keyData.key}
                      onClick={() => handleKeyPress(keyData.key)}
                      className="group relative size-16 rounded-full border-none bg-gray-800 text-white transition-all hover:scale-105 hover:bg-gray-700 active:scale-95"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <span className="text-2xl font-semibold">{keyData.key}</span>
                        {keyData.letters && (
                          <span className="text-[10px] font-light leading-tight text-gray-400">
                            {keyData.letters}
                          </span>
                        )}
                      </div>
                    </Button>
                  ))}
                </div>
              ))}
            </div>

            {/* Custom Prompt Toggle */}
            <div className="flex items-center justify-between rounded-lg bg-gray-900 p-4">
              <Label htmlFor="custom-prompt" className="cursor-pointer text-white">
                Use Custom Prompt
              </Label>
              <Switch
                id="custom-prompt"
                checked={useCustomPrompt}
                onCheckedChange={setUseCustomPrompt}
              />
            </div>

            {/* Custom Prompt Editor */}
            {useCustomPrompt && (
              <div className="space-y-2">
                <Label className="text-white">Custom Prompt</Label>
                <Textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Enter custom prompt to override the agent's system prompt..."
                  className="min-h-[120px] resize-none border-gray-700 bg-gray-900 text-white placeholder:text-gray-400"
                />
              </div>
            )}

            {/* Call Button */}
            <Button
              onClick={handleCall}
              disabled={!phoneNumber.trim() || loading}
              className="h-16 w-full rounded-full bg-green-500 text-xl font-semibold text-white shadow-lg transition-all hover:scale-105 hover:bg-green-600 hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
            >
              {loading ? (
                <div className="size-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <>
                  <Call2 width={24} height={24} className="mr-2" />
                  Call
                </>
              )}
            </Button>
          </TabsContent>

          <TabsContent value="contacts" className="mt-0 p-6">
            {loading ? (
              <div className="flex h-80 items-center justify-center">
                <div className="text-gray-400">Loading people...</div>
              </div>
            ) : (
              <>
                {/* Search */}
                <div className="relative mb-4">
                  <LinearSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width={16} height={16} />
                  <Input
                    placeholder="Search people..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-gray-700 bg-gray-900 pl-10 text-white placeholder:text-gray-500"
                  />
                </div>

                {/* Contacts List */}
                <ScrollArea className="h-80">
                  <div className="space-y-2">
                    {filteredContacts.map((contact) => (
                      <div
                        key={contact.id}
                        onClick={() => handleContactSelect(contact)}
                        className="flex cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors hover:bg-gray-800"
                      >
                        <Avatar className="size-10">
                          <AvatarFallback className="bg-gray-600 text-white">
                            {contact.first_name?.[0] || contact.last_name?.[0] || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-white">{contact.displayName}</div>
                          <div className="truncate text-sm text-gray-400">{formatPhoneNumber(contact.displayPhone)}</div>
                        </div>
                        <Call2 width={16} height={16} className="text-green-500" />
                      </div>
                    ))}
                    {filteredContacts.length === 0 && !loading && (
                      <div className="py-8 text-center text-gray-400">
                        {searchQuery ? "No contacts found" : "No contacts available"}
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </>
            )}
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  )
}
